import { templateSchema } from "./template-pages.mjs";
const sharedFamilies = new Set(["header", "heading", "tabs", "hint"]);
const slots = templateSchema.pages.flatMap((p) =>
  p.blocks.map((b) => ({
    key: `${p.id}:${b.id}`,
    pageId: p.id,
    blockId: b.id,
    name: b.name,
    pageName: p.name,
  })),
);
export function builtinFamily(renderer) {
  const slot = slots.find((s) => s.key === renderer);
  return slot
    ? sharedFamilies.has(slot.blockId)
      ? slot.blockId
      : slot.key
    : "";
}
export function builtinComponentMatches(component, pageId, blockId) {
  const family = builtinFamily(component.renderer);
  return (
    component.type === "builtin" &&
    !!family &&
    family === builtinFamily(`${pageId}:${blockId}`)
  );
}
export function builtinComponentPlacements(component) {
  const family = builtinFamily(component.renderer);
  return component.type === "builtin" && family
    ? slots.filter((s) => builtinFamily(s.key) === family)
    : [];
}
// Only presentation slots can cross page boundaries. Data lists, playback and
// forms retain their own business identity and filtering. Names do not affect
// equivalence; category, renderer, status and every parameter do.
const stable = (value) =>
  Array.isArray(value)
    ? value.map(stable)
    : value && typeof value === "object"
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map((key) => [key, stable(value[key])]),
        )
      : value;
export function consolidateBuiltinComponents(config) {
  const groups = new Map(),
    aliases = new Map(),
    components = [],
    merged = [];
  for (const c of config.components) {
    const family = c.type === "builtin" ? builtinFamily(c.renderer) : "";
    const key =
      family &&
      JSON.stringify(
        stable({
          family,
          categoryId: c.categoryId,
          rendererId: c.rendererId,
          status: c.status,
          params: c.params,
        }),
      );
    const existing = key && groups.get(key);
    if (existing) {
      aliases.set(c.id, existing.id);
      merged.push({ from: c.id, to: existing.id, name: c.name });
    } else {
      const copy = { ...c };
      components.push(copy);
      if (key) groups.set(key, copy);
    }
  }
  for (const c of components) {
    if (merged.some((m) => m.to === c.id)) {
      const label = slots.find((s) => s.key === c.renderer)?.name;
      // Keep administrator-defined names; replace automatically generated ones.
      if (slots.some((s) => c.name === `${s.pageName} / ${s.name}`))
        c.name = `共享${label}`;
    }
  }
  const templates = config.templates.map((t) => ({
    ...t,
    pages: t.pages.map((p) => ({
      ...p,
      blocks: p.blocks.map((b) =>
        aliases.has(b.componentId)
          ? { ...b, componentId: aliases.get(b.componentId) }
          : b,
      ),
    })),
  }));
  return { configuration: { ...config, components, templates }, merged };
}
export function bindBuiltinComponents(pages, components) {
  return pages.map((p) => ({
    ...p,
    blocks: p.blocks.map((b) => {
      if (components.some((c) => c.id === b.componentId)) return b;
      const component = components.find(
        (c) => c.status === "启用" && builtinComponentMatches(c, p.id, b.id),
      );
      return component ? { ...b, componentId: component.id } : b;
    }),
  }));
}
